import {Component, inject, OnDestroy, OnInit} from '@angular/core';
import {DatePipe} from '@angular/common';
import {FormBuilder, FormGroup, FormsModule, ReactiveFormsModule, Validators} from '@angular/forms';
import { AuthenticationService } from '@core/auth/authentication.service';
import { HasPermissionDirective } from '@shared/directives/has-permission.directive';
import {Subscription} from 'rxjs';
import {ObjetivosService} from '../objetivos.service';
import {alMenosUnObjetivoValidator, objetivoACheckboxes, objetivoDesdeCheckboxes} from '../objetivos.util';
import {ErrorService} from '../../../../../../../shared/services/error.service';
import {SnackbarService} from '@shared/services/snackbar.service';
import {MAT_DIALOG_DATA, MatDialogRef} from '@angular/material/dialog';
import {LabelComponent} from '../../../../../../../shared/components/form/label/label.component';
import {FormStatusService} from '../../../../../../../shared/services/form-status.service';
import {EditionButtons} from '../../../../../../../shared/components/ui/edition-buttons/edition-buttons';
import {Modal} from '@shared/components/common/modal/modal';
import {CombosService} from '../../../../../../../shared/services/combos.service';
import {GenericCombo} from '../../../../../../../shared/services/interfaces/combos.interface';
import {SelectFieldComponent, SelectOption} from '@shared/components/form/select/select-field.component';
import {InputFieldComponent} from '@shared/components/form/input/input-field.component';
import { AppFormComponent } from '@shared/components/ui/list-components';
import {DataTable, TableCellDirective, TableColumn} from '@shared/components/ui/data-table/data-table';
import {Pagination} from '@shared/components/ui/pagination/pagination';
import {Badge} from '@shared/components/ui/badge/badge';
import {ReadOnlyFieldComponent} from '@shared/components/ui/read-only-field/read-only-field.component';
import {ButtonComponent} from '@shared/components/ui/button/button.component';
import {ProgressBar} from '@shared/components/ui/progress-bar/progress-bar';
import {Objetivos, TipoTrabajoObjetivo, TrabajoObjetivo} from '../objetivos.interface';
import {avanceVisual, ESTADOS_SEGUIMIENTO} from '../seguimiento-objetivo.util';

@Component({
  selector: 'app-detalle-objetivo',
  imports: [
    HasPermissionDirective,
    LabelComponent,
    ReactiveFormsModule,
    EditionButtons,
    SelectFieldComponent,
    InputFieldComponent,
    AppFormComponent,
    Modal,
    FormsModule,
    DatePipe,
    DataTable,
    TableCellDirective,
    Pagination,
    Badge,
    ReadOnlyFieldComponent,
    ButtonComponent,
    ProgressBar,
  ],
  templateUrl: './detalle-objetivo.html',
  styles: ``
})
export class DetalleObjetivo implements OnInit, OnDestroy {
  readonly permissionAuth = inject(AuthenticationService);

  objetivo?: Objetivos;
  protected readonly estadosSeguimiento = ESTADOS_SEGUIMIENTO;
  protected readonly avanceVisual = avanceVisual;
  tipoTrabajo: TipoTrabajoObjetivo = 'aspiracion';
  trabajos: TrabajoObjetivo[] = [];
  loadingTrabajos = false;
  errorTrabajos = false;
  pagina = 1;
  porPagina = 15;
  ultimaPagina = 1;
  totalTrabajos = 0;
  private trabajosSubscription?: Subscription;
  readonly tipoTrabajoOptions: SelectOption[] = [
    {value: 'aspiracion', label: 'Aspiraciones'},
    {value: 'transferencia', label: 'Transferencias'},
  ];
  get columnasTrabajos(): TableColumn[] {
    return [
      {key: 'id', header: 'Trabajo #'},
      {key: 'agendamiento_id', header: 'Agendamiento #'},
      {key: 'fecha', header: 'Fecha'},
      {key: 'establecimiento', header: 'Establecimiento'},
      {key: 'animal', header: this.tipoTrabajo === 'aspiracion' ? 'Donante' : 'Receptora'},
      {key: 'resultado', header: 'Resultado'},
      ...(this.tipoTrabajo === 'transferencia' ? [{key: 'fecha_diagnostico', header: 'Fecha diagnóstico'}] : []),
    ];
  }

  cambiarTipoTrabajo(tipo: TipoTrabajoObjetivo): void {
    this.tipoTrabajo = tipo;
    this.cargarTrabajos(1);
  }

  cambiarPorPagina(cantidad: number): void {
    this.porPagina = cantidad;
    this.cargarTrabajos(1);
  }

  cargarTrabajos(pagina = this.pagina): void {
    this.trabajosSubscription?.unsubscribe();
    this.pagina = pagina;
    this.loadingTrabajos = true;
    this.errorTrabajos = false;
    this.trabajos = [];
    this.totalTrabajos = 0;
    this.trabajosSubscription = this.objetivosService.trabajos(this.objetivoId, this.tipoTrabajo, pagina, this.porPagina)
      .subscribe({
        next: (response) => {
          if (!response.status || !response.data) {
            this.errorTrabajos = true;
            return;
          }
          this.trabajos = response.data.trabajos;
          this.pagina = response.data.pagination.current_page;
          this.ultimaPagina = response.data.pagination.last_page;
          this.totalTrabajos = response.data.pagination.total;
        },
        error: (error) => {
          this.errorTrabajos = true;
          this.errorService.handleError(error);
        },
      });
    this.trabajosSubscription.add(() => { this.loadingTrabajos = false; });
  }

  ngOnDestroy(): void {
    this.trabajosSubscription?.unsubscribe();
  }

  editForm!: FormGroup;
  loading = false;
  // Se abre directo en edición (los trabajos considerados se ven igual): Cancelar cierra
  editable = !this.permissionAuth.canEdit('clientes');
  updating = false;
  clienteId!: number;
  objetivoId!: number;
  establecimientos: GenericCombo[] = [];

  get establecimientosOptions(): SelectOption[] { return this.establecimientos.map(x => ({ value: x.id, label: x.nombre })); }

  private fB = inject(FormBuilder);
  private fS = inject(FormStatusService);
  private objetivosService = inject(ObjetivosService);
  private combosService = inject(CombosService);
  private errorService = inject(ErrorService);
  private snackbarService = inject(SnackbarService);
  private dialogRef = inject(MatDialogRef<DetalleObjetivo>);
  private data = inject(MAT_DIALOG_DATA);

  ngOnInit(): void {
    this.clienteId = this.data.clienteId;
    this.objetivoId = this.data.objetivoId;
    this.makeForm();
    this.getCombos();
    this.getObjetivo();
  }

  private getCombos(): void {
    this.combosService.getEstablecimientosCombo(this.clienteId).subscribe({
      next: (response) => {
        this.establecimientos = response.data;
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    });
  }

  private makeForm(): void {
    this.editForm = this.fB.group({
      cli_establecimiento_id: [{value: null, disabled: this.editable}],
      // El objetivo se edita como dos casillas y se envía como un solo código A/T/X
      aspiracion: [{value: false, disabled: this.editable}],
      transferencia: [{value: false, disabled: this.editable}],
      cantidad_preneces_proyectadas: [{value: '', disabled: this.editable}, [Validators.required, Validators.min(0)]],
      cantidad_donantes: [{value: '', disabled: this.editable}, [Validators.required, Validators.min(0)]],
    }, { validators: alMenosUnObjetivoValidator() });
  }

  getObjetivo(): void {
    this.loading = true;
    this.objetivosService.show(this.objetivoId).subscribe({
      next: (response) => {
        if (response.status) {
          this.objetivo = response.data;
          this.tipoTrabajo = response.data.objetivo === 'T' ? 'transferencia' : 'aspiracion';
          this.cargarTrabajos(1);
          this.editForm.patchValue({
            ...response.data,
            ...objetivoACheckboxes(response.data.objetivo),
          });
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.loading = false;
    });
  }

  update(): void {
    if (!this.permissionAuth.canEdit('clientes')) return;
    this.updating = true;
    const {aspiracion, transferencia, ...resto} = this.editForm.value;
    const payload = {
      ...resto,
      objetivo: objetivoDesdeCheckboxes(aspiracion, transferencia)!,
    };

    this.objetivosService.update(this.objetivoId, payload).subscribe({
      next: (response) => {
        if (response.status) {
          this.snackbarService.showSnackBar('Objetivo actualizado correctamente');
          this.dialogRef.close(true);
        }
      }, error: (error) => {
        this.errorService.handleError(error);
      }
    }).add(() => {
      this.updating = false;
    });
  }

  editableSwitcher(): void {
    this.fS.changeStatus(this.editForm, this.editable);
    this.editable = !this.editable;
  }

  refreshObjetivo(): void {
    this.editableSwitcher();
    this.getObjetivo();
  }

  close(): void {
    this.dialogRef.close();
  }
}
